'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceFeature {
  label: string;
  included: boolean;
}

interface ServiceCardProps {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  alt: string;
  features: ServiceFeature[];
  benefits: string[];
  icon: string;
  detailsLink: string;
  pricing: {
    model: string;
    startingAt?: string;
  };
}

export default function ServiceCard({ 
  title, 
  category, 
  description, 
  image, 
  alt, 
  features, 
  benefits, 
  icon, 
  detailsLink,
  pricing 
}: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden border border-border group">
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 bg-brand-primary/90 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="text-xs font-cta font-semibold text-white">{category}</span>
        </div>
        <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
          <Icon name={icon as any} size={24} className="text-brand-primary" />
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        <h3 className="text-xl font-headline font-bold text-primary mb-2 group-hover:text-brand-primary transition-colors duration-200">
          {title}
        </h3>
        
        <p className="text-sm font-body text-muted-foreground mb-4 line-clamp-2">
          {description}
        </p>

        {/* Key Features */}
        <div className="space-y-2 mb-4">
          {features.slice(0, 3).map((feature, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Icon 
                name={feature.included ? "CheckCircleIcon" : "XCircleIcon"} 
                size={16} 
                variant="solid"
                className={feature.included ? "text-success mt-0.5" : "text-muted-foreground/40 mt-0.5"} 
              />
              <span className="text-xs font-body text-foreground">{feature.label}</span>
            </div>
          ))}
        </div>

        {/* Pricing Info */}
        <div className="bg-muted rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-body text-muted-foreground">Pricing Model</span>
            <span className="text-sm font-cta font-semibold text-primary">{pricing.model}</span>
          </div>
          {pricing.startingAt && (
            <div className="mt-1 text-xs font-body text-muted-foreground">
              Starting at <span className="font-semibold text-brand-secondary">{pricing.startingAt}</span>
            </div>
          )}
        </div>

        {/* Expandable Benefits */}
        {isExpanded && (
          <div className="mb-4 space-y-2 animate-fadeIn">
            <h4 className="text-sm font-headline font-semibold text-primary mb-2">Key Benefits:</h4>
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Icon name="SparklesIcon" size={14} className="text-brand-conversion mt-0.5" />
                <span className="text-xs font-body text-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <Link
            href={detailsLink}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-200 transform hover:scale-105"
          >
            <span>Learn More</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-2.5 border-2 border-border hover:border-brand-primary text-primary hover:text-brand-primary rounded-lg font-cta font-semibold text-sm transition-all duration-200"
            aria-label={isExpanded ? "Show less details" : "Show more details"}
          >
            <Icon name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}